import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { HardDrive, Plus, RefreshCw, ArrowLeft, Trash2, CheckCircle2, XCircle, Info, AlertTriangle } from 'lucide-react';
import { formatHardwareId, type ApplianceDetail, type ClaimApplianceRequest, type Entitlements, type PortalAppliance } from '@scada-ink/shared/cloud';
import { Button } from '@/components/ui';
import { Alert, TextField, Toggle } from '@/components/form';
import { usePageTitle } from '@/components/layout/SiteLayout';
import { cloudApi, errorMessage } from '@/lib/cloudApi';
import { useAsync } from '@/lib/useAsync';
import {
  Card,
  ConnectionBadge,
  EmptyState,
  HealthLabel,
  LineChart,
  LoadError,
  Loading,
  PortalHeader,
  StatCard,
  Table,
  formatDateTime,
  formatUptime,
  timeAgo,
} from './components';

function HowToConnect() {
  return (
    <div className="rounded-[var(--radius-glass-sm)] bg-ink-muted/70 border border-glass-border p-4 text-sm text-text-secondary">
      <div className="flex items-center gap-2 font-semibold text-text-primary">
        <Info size={15} className="text-brand-primary" /> Getting health reports from an appliance
      </div>
      <ol className="mt-2 ml-5 list-decimal space-y-1">
        <li>Appliances register automatically when you buy or store a license for them. You can also add one here by pasting any of its license keys.</li>
        <li>
          On the appliance, open <b>Appliance → System</b> and turn on <b>Cloud health reporting</b>.
        </li>
        <li>The appliance needs outbound HTTPS to cloud.scada.ink. It reports every minute; nothing is sent until you turn it on.</li>
      </ol>
    </div>
  );
}

function ClaimForm({ onDone, onCancel }: { onDone: (a: PortalAppliance) => void; onCancel: () => void }) {
  const [form, setForm] = useState<ClaimApplianceRequest>({ licenseKey: '', name: '', site: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onDone(await cloudApi.post<PortalAppliance>('/api/portal/appliances', { ...form, licenseKey: form.licenseKey.trim() }));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Add an appliance" className="mb-6">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <TextField label="Name" required placeholder="Line 2 — Molding" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <TextField label="Site" placeholder="Plant 1, Dayton" value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })} />
        </div>
        <TextField
          label="License key"
          required
          placeholder="SCADA-…"
          className="font-mono"
          value={form.licenseKey}
          onChange={(e) => setForm({ ...form, licenseKey: e.target.value })}
          hint="The key proves which appliance this is — each key is locked to one appliance’s hardware ID."
        />
        <HowToConnect />
        {error && <Alert kind="error">{error}</Alert>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? 'Adding…' : 'Add appliance'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

export function AppliancesPage() {
  usePageTitle('Appliances');
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const claiming = params.get('claim') === '1';
  const { data, error, loading, reload } = useAsync(() => cloudApi.get<PortalAppliance[]>('/api/portal/appliances'));

  const setClaiming = (on: boolean) => setParams(on ? { claim: '1' } : {}, { replace: true });

  return (
    <>
      <PortalHeader
        title="Appliances"
        description="Every appliance licensed to your account. Health appears once Cloud health reporting is turned on on the appliance and it can reach the internet."
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => void reload()}>
              <RefreshCw size={14} /> Refresh
            </Button>
            {!claiming && (
              <Button size="sm" onClick={() => setClaiming(true)}>
                <Plus size={14} /> Add appliance
              </Button>
            )}
          </>
        }
      />

      {claiming && <ClaimForm onCancel={() => setClaiming(false)} onDone={(a) => navigate(`/portal/appliances/${a.id}`)} />}

      {loading && !data ? (
        <Loading />
      ) : error || !data ? (
        <LoadError message={error ?? 'Could not load appliances.'} onRetry={reload} />
      ) : data.length === 0 ? (
        !claiming && (
          <Card>
            <EmptyState
              icon={HardDrive}
              title="No appliances registered"
              body="Add an appliance with its license key to monitor its health from anywhere."
              action={<Button onClick={() => setClaiming(true)}>Add appliance</Button>}
            />
          </Card>
        )
      ) : (
        <Card padded={false}>
          <Table head={['Appliance', 'Status', 'Health', 'CPU', 'Memory', 'Disk', 'Devices', 'Alarms', 'Last report']}>
            {data.map((a) => {
              const hb = a.latest;
              const pct = (v: number | undefined) => (v === undefined ? '—' : `${Math.round(v)}%`);
              return (
                <tr key={a.id} className="hover:bg-ink-muted/40 cursor-pointer" onClick={() => navigate(`/portal/appliances/${a.id}`)}>
                  <td className="px-5 py-3">
                    <Link to={`/portal/appliances/${a.id}`} className="font-semibold hover:text-brand-primary" onClick={(e) => e.stopPropagation()}>
                      {a.name}
                    </Link>
                    <div className="text-xs text-text-muted">{a.site || '—'}</div>
                    <div className="text-[11px] font-mono text-text-muted">{formatHardwareId(a.hardwareId)}</div>
                  </td>
                  <td className="px-5 py-3">
                    <ConnectionBadge status={a.status} />
                  </td>
                  <td className="px-5 py-3">
                    <HealthLabel health={a.health} />
                    {a.healthReasons.length > 0 && <div className="text-xs text-text-muted max-w-[14rem] truncate">{a.healthReasons.join(' · ')}</div>}
                  </td>
                  <td className="px-5 py-3 tabular-nums">{pct(hb?.cpuPct)}</td>
                  <td className="px-5 py-3 tabular-nums">{pct(hb?.memPct)}</td>
                  <td className="px-5 py-3 tabular-nums">{pct(hb?.diskPct)}</td>
                  <td className="px-5 py-3 tabular-nums">{hb ? `${hb.devices.connected}/${hb.devices.total}` : '—'}</td>
                  <td className={`px-5 py-3 tabular-nums ${hb && hb.activeAlarms > 0 ? 'text-status-idle font-semibold' : ''}`}>{hb ? hb.activeAlarms : '—'}</td>
                  <td className="px-5 py-3 text-text-secondary whitespace-nowrap">{timeAgo(a.lastSeenAt)}</td>
                </tr>
              );
            })}
          </Table>
        </Card>
      )}
    </>
  );
}

export function ApplianceDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data, error, loading, reload, setData } = useAsync(async () => {
    const [appliance, entitlements] = await Promise.all([
      cloudApi.get<ApplianceDetail>(`/api/portal/appliances/${encodeURIComponent(id)}`),
      cloudApi.get<Entitlements>('/api/portal/entitlements'),
    ]);
    return { appliance, entitlements };
  }, [id]);
  usePageTitle(data?.appliance.name ?? 'Appliance');

  const [edit, setEdit] = useState<{ name: string; site: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  if (loading && !data) return <Loading />;
  if (error || !data) return <LoadError message={error ?? 'Appliance not found.'} onRetry={reload} />;

  const { appliance: a, entitlements } = data;
  const hb = a.latest;

  async function patch(body: Partial<{ name: string; site: string; archiveEnabled: boolean; remoteEnabled: boolean }>) {
    setSaving(true);
    setActionError(null);
    try {
      const updated = await cloudApi.patch<PortalAppliance>(`/api/portal/appliances/${encodeURIComponent(id)}`, body);
      setData({ entitlements, appliance: { ...a, ...updated } });
      setEdit(null);
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!window.confirm(`Remove ${a.name} from your account? Its health history will no longer be visible here.`)) return;
    try {
      await cloudApi.del(`/api/portal/appliances/${encodeURIComponent(id)}`);
      navigate('/portal/appliances');
    } catch (err) {
      setActionError(errorMessage(err));
    }
  }

  const series = (key: 'cpuPct' | 'memPct' | 'diskPct' | 'cpuTempC') => a.history.map((p) => ({ at: p.at, value: p[key] }));

  return (
    <>
      <Link to="/portal/appliances" className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary mb-4">
        <ArrowLeft size={14} /> Appliances
      </Link>
      <PortalHeader
        title={a.name}
        description={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <ConnectionBadge status={a.status} />
            <HealthLabel health={a.health} />
            <span>{a.site || 'No site'}</span>
            <span className="font-mono text-xs">{formatHardwareId(a.hardwareId)}</span>
            <span>Last report {timeAgo(a.lastSeenAt)}</span>
          </span>
        }
        actions={
          <Button variant="secondary" size="sm" onClick={() => void reload()}>
            <RefreshCw size={14} /> Refresh
          </Button>
        }
      />

      {actionError && (
        <div className="mb-4">
          <Alert kind="error">{actionError}</Alert>
        </div>
      )}

      {a.status === 'never' && (
        <div className="mb-6">
          <HowToConnect />
        </div>
      )}

      {a.healthReasons.length > 0 && (
        <div className={`mb-6 rounded-[var(--radius-glass-sm)] border px-4 py-3 ${a.health === 'critical' ? 'border-status-fault/30 bg-status-fault/5' : 'border-status-idle/30 bg-status-idle/5'}`}>
          <div className={`flex items-center gap-2 text-sm font-semibold ${a.health === 'critical' ? 'text-status-fault' : 'text-status-idle'}`}>
            <AlertTriangle size={15} /> {a.health === 'critical' ? 'Needs attention now' : 'Worth a look'}
          </div>
          <ul className="mt-1.5 ml-6 list-disc text-sm text-text-secondary space-y-0.5">
            {a.healthReasons.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {hb && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <StatCard label="Devices connected" value={`${hb.devices.connected}/${hb.devices.total}`} tone={hb.devices.faulted > 0 ? 'warn' : 'good'} sub={hb.devices.faulted ? `${hb.devices.faulted} faulted` : 'None faulted'} />
            <StatCard label="Active alarms" value={hb.activeAlarms} tone={hb.activeAlarms > 0 ? 'warn' : 'good'} />
            <StatCard label="Uptime" value={formatUptime(hb.uptimeSec)} sub={`v${hb.softwareVersion}${hb.licenseTier ? ` · ${hb.licenseTier}` : ''}`} />
            <StatCard label="Last backup" value={hb.lastBackupAt ? timeAgo(hb.lastBackupAt) : '—'} sub={hb.lastBackupAt ? formatDateTime(hb.lastBackupAt) : 'No backup on record'} />
          </div>

          <h2 className="text-sm font-semibold mb-3">Last 24 hours</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <LineChart label="CPU" unit="%" max={100} points={series('cpuPct')} />
            <LineChart label="Memory" unit="%" max={100} points={series('memPct')} color="#7c3aed" />
            <LineChart label="Disk" unit="%" max={100} points={series('diskPct')} color="#ca8a04" />
            <LineChart label="CPU temperature" unit="°C" max={90} points={series('cpuTempC')} color="#dc2626" />
          </div>
        </>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Services">
          {!hb || hb.services.length === 0 ? (
            <p className="text-sm text-text-muted">No service status reported yet.</p>
          ) : (
            <ul className="space-y-2">
              {hb.services.map((s) => (
                <li key={s.name} className="flex items-center gap-2.5 text-sm">
                  {s.ok ? <CheckCircle2 size={16} className="text-status-running" /> : <XCircle size={16} className="text-status-fault" />}
                  <span className="font-medium capitalize">{s.name}</span>
                  {s.detail && <span className="text-text-muted text-xs">{s.detail}</span>}
                </li>
              ))}
            </ul>
          )}
          {hb && (
            <dl className="mt-5 pt-4 border-t border-glass-border grid grid-cols-2 gap-y-2 text-xs">
              <dt className="text-text-muted">Hostname</dt>
              <dd className="font-mono">{hb.hostname}</dd>
              {hb.model && (
                <>
                  <dt className="text-text-muted">Model</dt>
                  <dd>{hb.model}</dd>
                </>
              )}
              {hb.osVersion && (
                <>
                  <dt className="text-text-muted">OS</dt>
                  <dd>{hb.osVersion}</dd>
                </>
              )}
              <dt className="text-text-muted">Appliance ID</dt>
              <dd className="font-mono truncate">{hb.applianceId}</dd>
            </dl>
          )}
        </Card>

        <Card title="Settings">
          {edit ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void patch(edit);
              }}
              className="space-y-3"
            >
              <TextField label="Name" required value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
              <TextField label="Site" value={edit.site} onChange={(e) => setEdit({ ...edit, site: e.target.value })} />
              <div className="flex justify-end gap-2">
                <Button variant="secondary" size="sm" onClick={() => setEdit(null)}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={saving}>
                  Save
                </Button>
              </div>
            </form>
          ) : (
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <div className="font-semibold">{a.name}</div>
                <div className="text-text-muted">{a.site || 'No site'}</div>
              </div>
              <Button variant="secondary" size="sm" onClick={() => setEdit({ name: a.name, site: a.site })}>
                Rename
              </Button>
            </div>
          )}

          <div className="mt-5 pt-5 border-t border-glass-border space-y-4">
            <Toggle
              label="Cloud Data Archive"
              description={entitlements.archive.active ? `Covered by your plan (${entitlements.archive.appliances} appliance${entitlements.archive.appliances === 1 ? '' : 's'}).` : 'Requires an Archive plan.'}
              checked={a.archiveEnabled}
              disabled={saving || (!entitlements.archive.active && !a.archiveEnabled)}
              onChange={(v) => void patch({ archiveEnabled: v })}
            />
            <Toggle
              label="Remote Viewing"
              description={entitlements.remote.active ? `Covered by your plan (${entitlements.remote.appliances} appliance${entitlements.remote.appliances === 1 ? '' : 's'}).` : 'Requires a Remote View plan.'}
              checked={a.remoteEnabled}
              disabled={saving || (!entitlements.remote.active && !a.remoteEnabled)}
              onChange={(v) => void patch({ remoteEnabled: v })}
            />
          </div>

          <div className="mt-5 pt-5 border-t border-glass-border flex items-center justify-between gap-3">
            <span className="text-xs text-text-muted">Registered {formatDateTime(a.claimedAt)}</span>
            <button type="button" onClick={() => void remove()} className="inline-flex items-center gap-1.5 text-sm font-semibold text-status-fault hover:underline">
              <Trash2 size={14} /> Remove
            </button>
          </div>
        </Card>
      </div>
    </>
  );
}
